import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import orderService from '../services/orderService';
import clientService from '../services/clientService';
import productService from '../services/productService';
import useBarcodeScanner from '../hooks/useBarcodeScanner';
import toast from 'react-hot-toast';
import {
  HiOutlineArrowLeft,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineSearch,
} from 'react-icons/hi';
import './Orders.css';

const NewOrder = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchProduct, setSearchProduct] = useState('');
  const [scanFeedback, setScanFeedback] = useState(null);

  // Scanner: al escanear, buscar producto y agregarlo directo a la orden
  const handleBarcodeScan = useCallback(async (code) => {
    setScanFeedback({ code, status: 'searching' });
    try {
      const result = await productService.getByBarcode(code);
      if (result.data) {
        const product = result.data;
        const units = product.units_per_presentation || 1;
        const label = product.presentation_label || 'Unidad';
        const isBox = units > 1;

        setScanFeedback({ code, status: 'found', product });

        // Determinar precio según tipo de cliente seleccionado
        setForm((prev) => {
          const selectedClient = clients.find((c) => String(c.id) === String(prev.client_id));
          const isMayorista = selectedClient?.client_type === 'mayorista';
          const price = isMayorista
            ? Number(product.wholesale_price || product.unit_price || 0)
            : Number(product.retail_price || product.unit_price || 0);

          const exists = prev.items.find((i) => i.product_id === product.id);
          if (exists) {
            const msg = isBox
              ? `+${units} ${product.name} (${label})`
              : `+1 ${product.name}`;
            toast.success(msg, { icon: isBox ? '📦' : '✅' });
            return {
              ...prev,
              items: prev.items.map((i) =>
                i.product_id === product.id ? { ...i, quantity: i.quantity + units } : i
              ),
            };
          } else {
            const msg = isBox
              ? `${product.name} — ${label} (${units} und)`
              : `${product.name} agregado`;
            toast.success(msg, { icon: isBox ? '📦' : '✅' });
            return {
              ...prev,
              items: [
                ...prev.items,
                {
                  product_id: product.id,
                  product_name: product.name,
                  quantity: units,
                  unit_price: price,
                },
              ],
            };
          }
        });
        setTimeout(() => setScanFeedback(null), 1500);
      }
    } catch {
      setScanFeedback({ code, status: 'not-found' });
      toast.error(`Producto no encontrado: ${code}`);
      setTimeout(() => setScanFeedback(null), 3000);
    }
  }, [clients]);

  useBarcodeScanner(handleBarcodeScan);

  const [form, setForm] = useState({
    client_id: '',
    is_credit: false,
    requires_ncf: false,
    ncf_type: 'B02',
    notes: '',
    items: [],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientsRes, productsRes] = await Promise.all([
          clientService.getAll(),
          productService.getAll(),
        ]);
        setClients(clientsRes.data || clientsRes.clients || []);
        setProducts(productsRes.data || productsRes.products || []);
      } catch {
        toast.error('Error cargando datos');
      }
    };
    fetchData();
  }, []);

  const addItem = (product) => {
    // Determinar precio según tipo de cliente
    const selectedClient = clients.find((c) => String(c.id) === String(form.client_id));
    const isMayorista = selectedClient?.client_type === 'mayorista';
    const price = isMayorista
      ? Number(product.wholesale_price || product.unit_price || 0)
      : Number(product.retail_price || product.unit_price || 0);

    const exists = form.items.find((i) => i.product_id === product.id);
    if (exists) {
      setForm({
        ...form,
        items: form.items.map((i) =>
          i.product_id === product.id ? { ...i, quantity: i.quantity + 1 } : i
        ),
      });
    } else {
      setForm({
        ...form,
        items: [
          ...form.items,
          {
            product_id: product.id,
            product_name: product.name,
            quantity: 1,
            unit_price: price,
          },
        ],
      });
    }
    setSearchProduct('');
  };

  const updateItem = (index, field, value) => {
    const updated = [...form.items];
    updated[index] = { ...updated[index], [field]: Number(value) || 0 };
    setForm({ ...form, items: updated });
  };

  const removeItem = (index) => {
    setForm({ ...form, items: form.items.filter((_, i) => i !== index) });
  };

  const subtotal = form.items.reduce((sum, i) => sum + i.quantity * i.unit_price, 0);
  // ITBIS 18% solo cuando la orden requiere comprobante fiscal (NCF)
  const taxRate = form.requires_ncf ? 0.18 : 0;
  const tax = subtotal * taxRate;
  const total = subtotal + tax;

  const filteredProducts = products.filter((p) => {
    if (!searchProduct) return false;
    const q = searchProduct.toLowerCase();
    return (
      p.name?.toLowerCase().includes(q) ||
      p.sku?.toLowerCase().includes(q) ||
      p.barcode?.toLowerCase().includes(q)
    );
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.client_id) {
      toast.error('Selecciona un cliente');
      return;
    }
    if (form.items.length === 0) {
      toast.error('Agrega al menos un producto');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        client_id: Number(form.client_id),
        items: form.items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          unit_price: i.unit_price,
        })),
        is_credit: form.is_credit,
        requires_ncf: form.requires_ncf,
        ncf_type: form.requires_ncf ? form.ncf_type : undefined,
        notes: form.notes || undefined,
      };

      const result = await orderService.create(payload);
      toast.success('Orden creada exitosamente');
      navigate(`/ordenes/${result.data?.id || ''}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error creando orden');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="new-order">
      <div className="detail-header">
        <button className="btn-back" onClick={() => navigate('/ordenes')}>
          <HiOutlineArrowLeft /> Volver
        </button>
        <div className="detail-title">
          <h1>Nueva Orden</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="new-order-grid">
          {/* Panel izquierdo: cliente y opciones */}
          <div className="detail-card">
            <h3>Datos de la Orden</h3>

            <div className="form-group">
              <label>Cliente *</label>
              <select
                value={form.client_id}
                onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                required
              >
                <option value="">Seleccionar cliente...</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.client_type === 'mayorista' ? '🏢' : '🛒'} {c.contact_name || c.business_name || c.name} {c.rnc ? `(RNC: ${c.rnc})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.is_credit}
                  onChange={(e) => setForm({ ...form, is_credit: e.target.checked })}
                />
                <span>Venta a crédito</span>
              </label>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.requires_ncf}
                  onChange={(e) => setForm({ ...form, requires_ncf: e.target.checked })}
                />
                <span>Requiere NCF</span>
              </label>
            </div>

            {form.requires_ncf && (
              <div className="form-group">
                <label>Tipo de NCF</label>
                <select
                  value={form.ncf_type}
                  onChange={(e) => setForm({ ...form, ncf_type: e.target.value })}
                >
                  <option value="B01">B01 - Crédito Fiscal (requiere RNC)</option>
                  <option value="B02">B02 - Consumo</option>
                  <option value="B14">B14 - Régimen Especial</option>
                  <option value="B15">B15 - Gubernamental</option>
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Notas</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Instrucciones especiales..."
                rows={3}
              />
            </div>

            {/* Resumen */}
            <div className="order-summary">
              <div className="summary-row">
                <span>Subtotal</span>
                <span className="mono">RD$ {subtotal.toLocaleString('es-DO', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="summary-row">
                <span>ITBIS ({form.requires_ncf ? '18%' : '0% — Sin NCF'})</span>
                <span className="mono">RD$ {tax.toLocaleString('es-DO', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="summary-row total">
                <span>Total</span>
                <span className="mono">RD$ {total.toLocaleString('es-DO', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>

            <button
              type="submit"
              className="btn-primary full"
              disabled={loading}
            >
              {loading ? 'Creando...' : 'Crear Orden'}
            </button>
          </div>

          {/* Panel derecho: productos */}
          <div className="detail-card">
            <h3>Productos</h3>

            <div className={`scanner-indicator ${scanFeedback ? 'active' : ''}`}>
              <span className="scanner-dot" />
              <span>{scanFeedback ? `Escaneado: ${scanFeedback.code}` : 'Escáner listo — escanea un código de barras'}</span>
            </div>

            <div className="product-search">
              <HiOutlineSearch className="search-icon" />
              <input
                type="text"
                placeholder="Buscar por nombre, SKU o código de barras..."
                value={searchProduct}
                onChange={(e) => setSearchProduct(e.target.value)}
              />
              {filteredProducts.length > 0 && (
                <div className="product-dropdown">
                  {filteredProducts.map((p) => (
                    <div
                      key={p.id}
                      className="product-option"
                      onClick={() => addItem(p)}
                    >
                      <span className="product-option-name">{p.name}</span>
                      <span className="product-option-price">
                        RD$ {Number(p.unit_price || p.sale_price || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {form.items.length === 0 ? (
              <div className="empty-items">
                <HiOutlinePlus className="empty-icon" />
                <p>Busca y agrega productos a la orden</p>
              </div>
            ) : (
              <div className="items-list">
                {form.items.map((item, i) => (
                  <div key={i} className="item-row">
                    <div className="item-info">
                      <span className="item-name">{item.product_name}</span>
                      <span className="item-subtotal mono">
                        RD$ {(item.quantity * item.unit_price).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="item-controls">
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateItem(i, 'quantity', e.target.value)}
                        className="qty-input"
                      />
                      <span className="item-x">×</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => updateItem(i, 'unit_price', e.target.value)}
                        className="price-input"
                      />
                      <button
                        type="button"
                        className="btn-remove"
                        onClick={() => removeItem(i)}
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default NewOrder;
